import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface NavbarProps {
  scrolled: boolean;
}

export default function Navbar({ scrolled }: NavbarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const navLinks = [
    { label: '製品', href: '#products', type: 'anchor' as const },
    { label: 'MeVER Chair', to: '/chair', type: 'link' as const },
    { label: 'レンタル', to: '/rental', type: 'link' as const },
    { label: 'コワーキング', to: '/coworking', type: 'link' as const },
    { label: 'TEA', to: '/tea', type: 'link' as const },
    { label: 'Atelier Folio', to: '/atelier-folio', type: 'link' as const },
    { label: '運営会社', to: '/about-company', type: 'link' as const },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md shadow-sm'
            : 'bg-black/30 backdrop-blur-sm'
        }`}
        role="navigation"
        aria-label="メインナビゲーション"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex items-center h-16 sm:h-20">

            {/* Mobile: hamburger left */}
            <div className="md:hidden flex items-center w-10">
              <button
                className="w-10 h-10 flex items-center justify-center cursor-pointer"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="メニューを開く"
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-menu"
              >
                <i
                  className={`ri-menu-line text-2xl ${
                    scrolled ? 'text-black' : 'text-white'
                  }`}
                  aria-hidden="true"
                ></i>
              </button>
            </div>

            {/* PC: Logo left-aligned */}
            <div className="hidden md:flex items-center shrink-0">
              <Link to="/" aria-label="MeVER ホームページへ">
                <img
                  src="https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/9687a9ed07026a2b79edd442f7404cde.png?format=webp&quality=85"
                  alt="MeVER ロゴ"
                  className={`h-7 w-auto cursor-pointer transition-all duration-300 ${
                    scrolled ? '' : 'brightness-0 invert'
                  }`}
                  width="120"
                  height="28"
                  loading="eager"
                  decoding="async"
                />
              </Link>
            </div>

            {/* Mobile: Logo centered */}
            <div className="md:hidden flex-1 flex justify-center">
              <Link to="/" aria-label="MeVER ホームページへ">
                <img
                  src="https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/9687a9ed07026a2b79edd442f7404cde.png?format=webp&quality=85"
                  alt="MeVER ロゴ"
                  className={`h-6 w-auto cursor-pointer transition-all duration-300 ${
                    scrolled ? '' : 'brightness-0 invert'
                  }`}
                  width="100"
                  height="24"
                  loading="eager"
                  decoding="async"
                />
              </Link>
            </div>

            {/* Mobile: right spacer for centering */}
            <div className="md:hidden w-10"></div>

            {/* PC: Desktop navigation - horizontal row */}
            <div className="hidden md:flex items-center ml-10 lg:ml-14 space-x-5 lg:space-x-8 flex-1">
              {navLinks.map((item) =>
                item.type === 'anchor' ? (
                  <a
                    key={item.label}
                    href={item.href}
                    className={`text-[13px] font-light tracking-[0.08em] transition-colors cursor-pointer whitespace-nowrap ${
                      scrolled
                        ? 'text-black/70 hover:text-black'
                        : 'text-white/80 hover:text-white'
                    }`}
                  >
                    {item.label}
                  </a>
                ) : (
                  <Link
                    key={item.label}
                    to={item.to!}
                    className={`text-[13px] font-light tracking-[0.08em] transition-colors cursor-pointer whitespace-nowrap ${
                      scrolled
                        ? 'text-black/70 hover:text-black'
                        : 'text-white/80 hover:text-white'
                    }`}
                  >
                    {item.label}
                  </Link>
                )
              )}
            </div>

            {/* PC: Right CTA */}
            <div className="hidden md:flex items-center gap-3 shrink-0">
              <Link
                to="/campaign"
                className={`inline-flex items-center gap-1.5 px-3 lg:px-4 py-1.5 text-[12px] font-medium tracking-wide transition-all whitespace-nowrap rounded-full cursor-pointer border ${
                  scrolled
                    ? 'border-[#C45C5C]/30 text-[#C45C5C] bg-[#C45C5C]/5 hover:bg-[#C45C5C]/10'
                    : 'border-[#FF8A8A]/40 text-[#FF8A8A] bg-white/10 hover:bg-white/15'
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${scrolled ? 'bg-[#C45C5C]' : 'bg-[#FF8A8A]'}`}></span>
                4/1 価格改定
              </Link>
              <a
                href="https://appointment.mever.design/"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className={`inline-flex items-center px-4 lg:px-5 py-2 text-[13px] font-light tracking-[0.08em] transition-all whitespace-nowrap rounded-md cursor-pointer ${
                  scrolled
                    ? 'bg-black text-white hover:bg-black/80'
                    : 'bg-white text-black hover:bg-white/90'
                }`}
              >
                <i className="ri-calendar-check-line mr-1.5" aria-hidden="true"></i>
                体験予約
              </a>
              <a
                href="https://tihhyh.readdy.co"
                target="_blank"
                rel="noopener noreferrer"
                className={`px-4 lg:px-5 py-2 text-[13px] font-light tracking-[0.08em] transition-all whitespace-nowrap rounded-md cursor-pointer border ${
                  scrolled
                    ? 'border-black/20 text-black/70 hover:bg-black/5'
                    : 'border-white/30 text-white/80 hover:bg-white/10'
                }`}
                aria-label="法人のお客様向けページを新しいタブで開く"
              >
                法人のお客様
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 z-[60] transition-opacity duration-300 md:hidden ${
          mobileMenuOpen
            ? 'opacity-100 visible'
            : 'opacity-0 invisible pointer-events-none'
        }`}
        onClick={() => setMobileMenuOpen(false)}
        aria-hidden="true"
      />

      {/* Mobile Menu Panel - slide from left */}
      <div
        id="mobile-menu"
        className={`fixed top-0 left-0 h-full w-80 max-w-[85vw] bg-white z-[70] transform transition-transform duration-300 ease-out md:hidden ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="モバイルメニュー"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between px-5 h-16 border-b border-gray-100">
            <img
              src="https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/9687a9ed07026a2b79edd442f7404cde.png?format=webp&quality=85"
              alt="MeVER ロゴ"
              className="h-6 w-auto"
              width="100"
              height="24"
              loading="lazy"
              decoding="async"
            />
            <button
              className="w-10 h-10 flex items-center justify-center cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="メニューを閉じる"
            >
              <i className="ri-close-line text-2xl text-black" aria-hidden="true"></i>
            </button>
          </div>

          {/* Menu Items */}
          <nav
            className="flex-1 px-5 py-6 overflow-y-auto"
            aria-label="モバイルナビゲーション"
          >
            <ul className="space-y-0.5">
              <li>
                <a
                  href="#products"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-3.5 text-[15px] font-light text-black/80 hover:text-black transition-colors border-b border-gray-50 cursor-pointer tracking-wide"
                >
                  製品
                </a>
              </li>
              <li>
                <Link
                  to="/chair"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-3.5 text-[15px] font-light text-black/60 hover:text-black transition-colors border-b border-gray-50 cursor-pointer pl-4 tracking-wide"
                >
                  MeVER Chair
                </Link>
              </li>
              <li>
                <Link
                  to="/rental"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-3.5 text-[15px] font-light text-black/60 hover:text-black transition-colors border-b border-gray-50 cursor-pointer pl-4 tracking-wide"
                >
                  レンタルサービス
                </Link>
              </li>
              <li>
                <Link
                  to="/coworking"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-3.5 text-[15px] font-light text-black/60 hover:text-black transition-colors border-b border-gray-50 cursor-pointer pl-4 tracking-wide"
                >
                  コワーキング体験（新宿）
                </Link>
              </li>
              <li>
                <Link
                  to="/tea"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-3.5 text-[15px] font-light text-black/60 hover:text-black transition-colors border-b border-gray-50 cursor-pointer pl-4 tracking-wide"
                >
                  TEA サブスク
                </Link>
              </li>
              <li>
                <Link
                  to="/atelier-folio"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-3.5 text-[15px] font-light text-black/60 hover:text-black transition-colors border-b border-gray-50 cursor-pointer pl-4 tracking-wide"
                >
                  Atelier Folio
                  <span className="ml-2 inline-flex items-center px-1.5 py-0.5 bg-emerald-500/10 text-emerald-600 text-[10px] font-medium rounded-full">NEW</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/accessories"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-3.5 text-[15px] font-light text-black/60 hover:text-black transition-colors border-b border-gray-50 cursor-pointer pl-4 tracking-wide"
                >
                  アクセサリー
                </Link>
              </li>
              <li>
                <Link
                  to="/about-company"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-3.5 text-[15px] font-light text-black/80 hover:text-black transition-colors border-b border-gray-50 cursor-pointer tracking-wide"
                >
                  運営会社
                </Link>
              </li>
            </ul>
          </nav>

          {/* CTA Buttons */}
          <div className="px-5 pb-6 space-y-2.5">
            <Link
              to="/campaign"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-center gap-2 w-full py-3.5 bg-[#C45C5C] text-white text-sm font-medium tracking-wide hover:bg-[#B04E4E] transition-colors whitespace-nowrap rounded-lg cursor-pointer"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
              4/1価格改定 — 価格維持予約はこちら
            </Link>
            <a
              href="https://appointment.mever.design/"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="flex items-center justify-center w-full py-3.5 bg-black text-white text-sm font-light tracking-wide hover:bg-black/80 transition-colors whitespace-nowrap rounded-lg cursor-pointer"
            >
              <i className="ri-calendar-check-line mr-1.5" aria-hidden="true"></i>
              MeVER Chair 体験予約
            </a>
            <a
              href="https://tihhyh.readdy.co"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full py-3.5 border border-black/15 text-black/70 text-center text-sm font-light tracking-wide hover:bg-black/5 transition-colors whitespace-nowrap rounded-lg cursor-pointer"
              aria-label="法人のお客様向けページを新しいタブで開く"
            >
              法人のお客様
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
